import { supabase } from "../../supabase/supabase";

const normalizeBaseUrl = (url: string) => url.replace(/\/+$/, "");

export type AgentPublishSource = "voxi" | "supabase";
export type AgentPublishType = "voice" | "video";
export type AgentPublishFeatureType = "job" | "campaign";
export type AgentPublishVersion = 1 | 2;

export interface PublishAgentRequest {
  name: string;
  sourceId: string;
  source?: AgentPublishSource;
  type?: AgentPublishType;
  featureType?: AgentPublishFeatureType;
  version?: AgentPublishVersion;
}

export async function publishAgent({
  name,
  sourceId,
  source = "voxi",
  type = "voice",
  featureType = "job",
  version = 1,
}: PublishAgentRequest) {
  // No default: a missing URL must fail loudly rather than publish to prod.
  const configuredUrl = import.meta.env.VITE_AGENT_PUBLISH_API_URL;
  if (!configuredUrl) {
    throw new Error(
      "Agent publishing is not configured (VITE_AGENT_PUBLISH_API_URL is not set)"
    );
  }
  const baseUrl = normalizeBaseUrl(configuredUrl);
  const configuredToken = import.meta.env.VITE_AGENT_PUBLISH_TOKEN;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = configuredToken || session?.access_token;

  if (!token) {
    throw new Error("No auth token available for publishing");
  }

  const response = await fetch(`${baseUrl}/voice-agents`, {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
      Authorization: `${token}`,
    },
    body: JSON.stringify({
      name,
      source,
      source_id: sourceId,
      type,
      feature_type: featureType,
      version,
    }),
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      result?.detail ||
        result?.error ||
        result?.message ||
        "Failed to publish assistant"
    );
  }

  return result;
}

// The assistant pickers list only published assistants
export async function markAssistantPublished(assistantId: string) {
  const { error } = await supabase
    .from("assistants")
    .update({ status: "published", updated_at: new Date().toISOString() })
    .eq("id", assistantId);

  if (error) {
    throw error;
  }
}
